import React from 'react';
import { ToolResultView } from '../../../ToolExecution/ToolResultView';
import { getRenderer } from '../../../tool-results/registry';
import type {
  ToolResultBlock,
  ToolUseBlock,
} from '../../../../store/chatStore';
import type { VisualizationMeta } from '../../../tool-results/visualization-schema';
import { assetsVisualizationMeta } from '../../../tool-results/config/assetsVisualization';
import { safeJsonParse } from '../../../../../../utils/jsonRepairUtils';
import { ErrorBoundary } from '../ErrorBoundary';
import { useDebouncedValue } from '../useDebouncedValue';
import { useToolResultContent } from '../../ToolResultDataContext';

interface MarkdownAssetsProps {
  code: string;
}

interface AssetsToolResultContent {
  data: Record<string, unknown>[];
  meta?: {
    page?: number;
    perPage?: number;
    total?: number;
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
  };
}

const MARKDOWN_ASSETS_TOOL_USE_ID = 'markdown-assets-tool-use';

const Skeleton = () => {
  return (
    <div className="my-4 overflow-hidden rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-all duration-200 dark:bg-gray-900">
      <div className="animate-pulse space-y-3">
        <div className="h-8 w-1/3 rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-6 w-full rounded bg-gray-100 dark:bg-gray-800" />
        <div className="h-6 w-11/12 rounded bg-gray-100 dark:bg-gray-800" />
      </div>
    </div>
  );
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getVisualizationMeta(value: unknown): VisualizationMeta | undefined {
  if (!isRecord(value) || !('primaryVisualization' in value)) {
    return undefined;
  }
  return value as unknown as VisualizationMeta;
}

function getAssetsToolResultContent(
  value: unknown,
): AssetsToolResultContent | undefined {
  if (!isRecord(value) || !Array.isArray(value.data)) {
    return undefined;
  }

  const content: AssetsToolResultContent = {
    data: value.data as Record<string, unknown>[],
  };

  if (isRecord(value.meta)) {
    content.meta = value.meta as AssetsToolResultContent['meta'];
  }

  return content;
}

function extractAssetsPayload(parsed: unknown): {
  content: AssetsToolResultContent;
  visualization?: VisualizationMeta;
} | null {
  if (Array.isArray(parsed)) {
    return {
      content: {
        data: parsed as Record<string, unknown>[],
        meta: {
          page: 1,
          perPage: parsed.length,
          total: parsed.length,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      },
    };
  }

  if (!isRecord(parsed)) {
    return null;
  }

  const directContent = getAssetsToolResultContent(parsed);
  if (directContent) {
    return {
      content: directContent,
      visualization: getVisualizationMeta(parsed.visualization),
    };
  }

  const wrappedContent = getAssetsToolResultContent(parsed.content);
  if (wrappedContent) {
    return {
      content: wrappedContent,
      visualization: getVisualizationMeta(parsed.visualization),
    };
  }

  return null;
}

/**
 * Merge asset data from the fenced code block with the real toolResult.
 *
 * The LLM often writes a simplified payload in the fenced block (omitting
 * resourceTags, riskScore, etc.) while the toolResult has the full data.
 * We merge per-item by matching on `id`: the toolResult item provides the
 * base, and the fenced-block item overlays any fields it explicitly carries.
 */
function mergeWithToolResult(
  fencedItems: Record<string, unknown>[],
  toolResultContent: string | undefined,
): Record<string, unknown>[] {
  if (!toolResultContent) {
    return fencedItems;
  }

  const toolParsed = safeJsonParse<unknown>(
    toolResultContent,
    'assets toolResult merge',
  );
  const toolPayload = extractAssetsPayload(toolParsed);
  if (!toolPayload || toolPayload.content.data.length === 0) {
    return fencedItems;
  }

  // Build a lookup from the full toolResult items keyed by id
  const toolItemById = new Map<string, Record<string, unknown>>();
  for (const item of toolPayload.content.data) {
    const id = typeof item.id === 'string' ? item.id : String(item.id ?? '');
    if (id) {
      toolItemById.set(id, item);
    }
  }

  if (toolItemById.size === 0) {
    return fencedItems;
  }

  return fencedItems.map((fenced) => {
    const id =
      typeof fenced.id === 'string' ? fenced.id : String(fenced.id ?? '');
    const toolItem = id ? toolItemById.get(id) : undefined;
    if (!toolItem) {
      return fenced;
    }
    // toolItem provides the base (all fields); fenced overlays on top so any
    // values the LLM explicitly included take precedence.
    return { ...toolItem, ...fenced };
  });
}

const AssetsRenderer = ({
  code,
  toolResultContent,
}: {
  code: string;
  toolResultContent: string | undefined;
}) => {
  const parsed = safeJsonParse<unknown>(code, 'assets markdown');
  const payload = extractAssetsPayload(parsed);

  if (!payload) {
    return <Skeleton />;
  }

  const mergedData = mergeWithToolResult(
    payload.content.data,
    toolResultContent,
  );

  const mergedContent: AssetsToolResultContent = {
    data: mergedData,
    meta: payload.content.meta,
  };

  const toolUse: ToolUseBlock = {
    type: 'toolUse',
    id: MARKDOWN_ASSETS_TOOL_USE_ID,
    name: 'listAssets',
    input: {},
    meta: {
      humanReadableName: 'List Plerion assets',
      source: 'plerion',
      visualization: payload.visualization ?? assetsVisualizationMeta,
    },
  };

  const result: ToolResultBlock = {
    type: 'toolResult',
    toolUseId: MARKDOWN_ASSETS_TOOL_USE_ID,
    toolName: 'listAssets',
    content: JSON.stringify(mergedContent),
    isError: false,
  };

  return (
    <div className="not-prose">
      <ToolResultView
        result={result}
        toolUse={toolUse}
        getRenderer={getRenderer}
      />
    </div>
  );
};

export const MarkdownAssets: React.FC<MarkdownAssetsProps> = ({ code }) => {
  const debouncedCode = useDebouncedValue(code, 300, 500);
  const toolResultContent = useToolResultContent('listAssets');

  return (
    <div className="not-prose mb-4">
      <ErrorBoundary
        onError={(error, errorInfo) => {
          console.error('Assets markdown rendering error:', error, errorInfo);
        }}
        resetKey={debouncedCode}
        fallback={<Skeleton />}
      >
        <AssetsRenderer
          code={debouncedCode}
          toolResultContent={toolResultContent}
        />
      </ErrorBoundary>
    </div>
  );
};

export default MarkdownAssets;
